import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { FeedRunCard } from "@signalist/shared-types";

import { useAppSelector } from "@/redux/hooks";
import {
	useIgnoreFeedRunMutation,
	useUndoIgnoreFeedRunMutation,
} from "@/pages/Private/redux/feed/feed.api";
import { feedSelector } from "@/pages/Private/redux/feed/feed.slice";

/** Undo window of the ignore toast; the error toast clears a bit faster. */
const UNDO_TOAST_MS = 6000;
const ERROR_TOAST_MS = 4000;

interface FeedRunIgnore {
	ignoreRun: (card: FeedRunCard) => Promise<void>;
	undoState: { runId: number; companyName: string | null } | null;
	undoIgnore: () => Promise<void>;
	isUndoing: boolean;
	ignoreError: string | null;
}

/**
 * Ignore-this-time with undo (D3). The POST's fulfilled matcher removes the
 * card and applies the fresh counts in the slice; this hook owns the toast
 * lifetime and the undo DELETE (whose `Feed` invalidation refetches the page).
 * A failed POST only toasts the error — nothing changes.
 */
export const useFeedRunIgnore = (): FeedRunIgnore => {
	const { t } = useTranslation();
	const { selectedLists } = useAppSelector(feedSelector);
	const [ignoreFeedRun] = useIgnoreFeedRunMutation();
	const [undoIgnoreFeedRun, { isLoading: isUndoing }] = useUndoIgnoreFeedRunMutation();

	const [undoState, setUndoState] = useState<FeedRunIgnore["undoState"]>(null);
	const [ignoreError, setIgnoreError] = useState<string | null>(null);
	const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!undoState && !ignoreError) {
			return;
		}

		toastTimerRef.current = setTimeout(
			() => {
				setUndoState(null);
				setIgnoreError(null);
			},
			undoState ? UNDO_TOAST_MS : ERROR_TOAST_MS
		);

		return () => {
			if (toastTimerRef.current) {
				clearTimeout(toastTimerRef.current);
			}
		};
	}, [undoState, ignoreError]);

	const listIds = selectedLists.map((list) => Number(list.id));

	const ignoreRun = async (card: FeedRunCard) => {
		// A runless card (PREC-3584) has no run to ignore — its ignore button is hidden, so
		// this is a defensive no-op should it ever be invoked.
		if (card.runId === null) {
			return;
		}

		setIgnoreError(null);

		try {
			await ignoreFeedRun({ runId: card.runId, listIds }).unwrap();
			setUndoState({ runId: card.runId, companyName: card.company.name });
		} catch {
			setUndoState(null);
			setIgnoreError(t("errors.wentWrong"));
		}
	};

	const undoIgnore = async () => {
		if (!undoState) {
			return;
		}

		try {
			await undoIgnoreFeedRun({ runId: undoState.runId, listIds }).unwrap();
		} catch {
			setIgnoreError(t("errors.wentWrong"));
		} finally {
			setUndoState(null);
		}
	};

	return { ignoreRun, undoState, undoIgnore, isUndoing, ignoreError };
};
